import React, { useCallback } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import { useOptionalField } from './FormikFields';
import { Trans } from 'react-i18next';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  radio: { display: 'none' },
  label: {
    position: 'relative',

    minWidth: theme.marginBase * 17,
    height: theme.height.regular,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase,

    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.colors.grayBorder,
    color: 'transparent',

    transition: 'border-color .1s, background-color .1s',

    marginRight: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
    '&:hover': {
      borderColor: theme.colors.green,
    },
  },
  text: {
    ...theme.absoluteFill,
    color: theme.colors.inkPrimary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  labelSelected: {
    backgroundColor: theme.colors.greenMuted,
    borderColor: theme.colors.green,
  },
  labelBlue: {
    backgroundColor: theme.colors.white,
    '&:hover': {
      borderColor: theme.colors.blue,
    },
  },
  labelSelectedBlue: {
    backgroundColor: theme.colors.blueMuted,
    borderColor: theme.colors.blue,
  },
  labelDisabled: {
    opacity: 0.5,
    cursor: 'auto',
    '&:hover': {
      borderColor: theme.colors.grayBorder,
    },
  },
  labelError: {
    backgroundColor: theme.colors.redMuted,
    borderColor: theme.colors.red,
    '&:hover': {
      borderColor: theme.colors.red,
    },
  },
}));

interface Props {
  name?: string;
  label: string;
  placeholder?: string;
  value: string | number;
  selected?: boolean;
  color?: Colors;
  onChange?: any;
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
  labelClassName?: string;
  id?: string;
}
export const RadioBlock = ({
  label,
  placeholder,
  value,
  selected: selectedProps,
  onChange,
  name,
  color,
  disabled,
  hasError,
  className,
  labelClassName,
  id,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const { helpers, field } = useOptionalField({ name });
  const selected =
    selectedProps !== undefined ? selectedProps : field.value === value;

  const _onChange = useCallback(
    (e: any) => {
      if (field.onChange) {
        field.onChange(e);
      }
      if (helpers) {
        helpers.setValue(value);
      }
      if (onChange) onChange(e);
    },
    [field, onChange, value, helpers],
  );

  return (
    <label
      className={classnames(classes.label, className, {
        [classes.labelSelected]: selected,
        [classes.labelSelectedBlue]: selected && color === 'blue',
        [classes.labelBlue]: color === 'blue',
        [classes.labelDisabled]: disabled,
        [classes.labelError]: hasError,
      })}
    >
      <input
        type="radio"
        {...field}
        id={id}
        onChange={_onChange}
        value={value}
        checked={selected}
        className={classes.radio}
        disabled={disabled}
      />
      {placeholder}
      <div className={classnames(classes.text, labelClassName)}>
        <Trans i18nKey={label}>{label}</Trans>
      </div>
    </label>
  );
};
